import { isNotNullOrUndefined } from './utils';
import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import { CoreEntitiesFilter } from '@/common/classes';

export class CoreQuery {
    private namespace$!: string;
    private relations$!: {
        relation: string;
        id?: string | number;
    };
    private readonly entities$!: Array<string | number>;
    private params$!: { [key: string]: any };
    private headers$!: { [key: string]: any };
    private data$: any;
    private cancelTokenSource$: CancelTokenSource;
    private axiosConfig: AxiosRequestConfig = {};

    constructor(apiVersion = 1) {
        this.entities$ = [];
        this.params$ = {};
        this.headers$ = { 'api-version': apiVersion };
        this.cancelTokenSource$ = axios.CancelToken.source();
    }

    public param(paramKey: string, paramValue): CoreQuery;
    public param(param: { [key: string]: any } | Array<{ [key: string]: any }>): CoreQuery;
    public param(param: { [key: string]: any } | Array<{ [key: string]: any }> | string, value?: any): CoreQuery {
        if (typeof param === 'string') {
            this.params$[param] = value;
        } else if (Array.isArray(param)) {
            param.forEach(p => this.param(p));
        } else {
            this.params$ = {
                ...this.params$,
                ...param,
            };
        }

        return this;
    }

    public headers(headerKey: string, headerValue);
    public headers(headers: { [key: string]: any } | Array<{ [key: string]: any }>);
    public headers(headers: { [key: string]: any } | Array<{ [key: string]: any }> | string, value?: any) {
        if (typeof headers === 'string') {
            this.headers$[headers] = value;
        } else if (Array.isArray(headers)) {
            headers.forEach(h => this.headers(h));
        } else {
            this.headers$ = {
                ...this.headers$,
                ...headers,
            };
        }

        return this;
    }

    public namespace(namespace: string): CoreQuery {
        this.namespace$ = namespace;

        return this;
    }

    public cancelTokenSource(token: CancelTokenSource) {
        this.cancelTokenSource$ = token;
    }

    public relations(relation: string, id?: string | number): CoreQuery {
        this.relations$ = {
            relation,
        };

        if (isNotNullOrUndefined(id)) {
            this.relations$.id = id;
        }

        return this;
    }

    public entity(entity: string | number | Array<string | number>): CoreQuery {
        if (Array.isArray(entity)) {
            this.entities$.push(...entity);
        } else {
            this.entities$.push(entity);
        }

        return this;
    }

    public path(entity: string | number | Array<string | number>): CoreQuery {
        return this.entity(entity);
    }

    public filters(coreFilter?: CoreEntitiesFilter): CoreQuery {
        if (coreFilter) {
            const filters = coreFilter.toQueryStringSearch();
            if (filters) {
                this.param({
                    filters,
                });
            }
        }

        return this;
    }

    public sorts(filters: CoreEntitiesFilter): CoreQuery {
        this.param({
            sorts: filters ? filters.toQueryStringSort() : null,
        });

        return this;
    }

    public onUploadProgress(callback: (progressValue: number) => any) {
        this.axiosConfig.onUploadProgress = progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            callback(percentCompleted);
        };
    }

    public data(data: any): CoreQuery {
        this.data$ = data;

        return this;
    }

    public toString(): string {
        const result: Array<string | number> = [];

        result.push(this.namespace$);

        if (isNotNullOrUndefined(this.relations$)) {
            result.push('relations', this.relations$.relation);

            if (isNotNullOrUndefined(this.relations$.id)) {
                result.push(this.relations$.id!);
            }
        }

        if (this.entities$.length > 0) {
            result.push(...this.entities$);
        }

        return result.join('/');
    }

    public get stringParams(): string {
        return Object.keys(this.params$)
            .filter(
                p =>
                    Object.prototype.hasOwnProperty.call(this.params$, p) &&
                    isNotNullOrUndefined(this.params$[p]) &&
                    this.params$[p] !== ''
            )
            .map(p => {
                if (Array.isArray(this.params$[p])) {
                    return this.params$[p].map(item => `${p}=${item}`).join('&')
                }
                return `${p}=${this.params$[p]}`
            })
            .join('&');


    }

    public getCustomConfig(config: Record<string, unknown>) {
        return {
            ...this.axiosConfig,
            headers: this.rawHeaders,
            data: this.data$,
            cancelToken: this.cancelTokenSource$.token,
            ...config,
        };
    }

    public get rawParams(): any {
        return this.params$;
    }

    public get query(): string {
        return this.toString();
    }

    public get rawHeaders(): any {
        return this.headers$;
    }

    public get axiosConfigParams(): AxiosRequestConfig {
        return {
            ...this.axiosConfig,
            params: this.rawParams,
            paramsSerializer: () => this.stringParams,
            headers: this.rawHeaders,
            data: this.data$,
            cancelToken: this.cancelTokenSource$.token,
        };
    }

    public cancelRequest() {
        if (this.cancelTokenSource$) {
            this.cancelTokenSource$.cancel();
        }
    }

    public clone(): CoreQuery {
        const q = new CoreQuery();

        q.namespace(this.namespace$);
        if (this.relations$) q.relations(this.relations$.relation, this.relations$.id);
        q.param(this.params$);

        q.entities$.push(...this.entities$);

        return q;
    }

    /*
        Static method to remove the necessity to invoke 'new CoreQuery()' every time
    */
    public static fromNamespace(namespace: string): CoreQuery {
        const q = new CoreQuery();
        q.namespace(namespace);

        return q;
    }
}
